import { useCurrentUser } from '@mp/hooks/useCurrentUser';
import { useProNavigate } from '@mp/contexts';
import { useProfessionalSaasDetail } from './useProfessionalSaasDetail';
import { useSchedulingPrefsConfig } from './useSchedulingPrefsConfig';
import { useSchedulingPreferencesSubmit } from '../mutations';
import { MedelyDateTime } from '@medely/date-time-tools';
import type { ProfessionalLocationJobSlot } from '@mp/graphql/generated/graphql';

type UseSchedulingPreferencesFormOptions = {
  onSuccess?: () => void;
};

export type SchedulingPreferencesFormValues = {
  labels: string[];
  workdays: string[];
  positions: string[];
  preferred_weekly_total_shifts: number;
};

const buildOptions = <T>(
  items: T[] | null | undefined,
  getLabel: (item: T) => string,
  getValue: (item: T) => string,
) => (items ?? []).map((item) => ({ label: getLabel(item), value: getValue(item) }));

const formatLastUpdatedText = (
  updatedAt: string | null | undefined,
  accountName: string | null | undefined,
): string | null => {
  if (!updatedAt) {
    return null;
  }
  const dt = new MedelyDateTime(updatedAt, { tz: 'system' });
  const formattedDate = dt.format('M/d/yy');
  const formattedTime = dt.format('h:mma').toLowerCase();
  const namePrefix = accountName ? `${accountName}, ` : '';
  return `Last updated: ${namePrefix}${formattedDate}, ${formattedTime}`;
};

const hasFormChanged = (
  current: SchedulingPreferencesFormValues,
  initial: SchedulingPreferencesFormValues,
): boolean => {
  const sortedStr = (arr: (string | number)[]) => [...arr].sort().join(',');
  return (
    sortedStr(current.labels) !== sortedStr(initial.labels) ||
    sortedStr(current.workdays) !== sortedStr(initial.workdays) ||
    sortedStr(current.positions) !== sortedStr(initial.positions) ||
    current.preferred_weekly_total_shifts !== initial.preferred_weekly_total_shifts
  );
};

export const useSchedulingPreferencesForm = ({
  onSuccess,
}: UseSchedulingPreferencesFormOptions = {}) => {
  const { currentUser } = useCurrentUser();
  const { navigate } = useProNavigate();
  const resolvedOnSuccess = onSuccess ?? (() => navigate('Profile'));
  const { professionalSaasDetail, isLoading } = useProfessionalSaasDetail(
    currentUser?.professional?.id,
  );
  const { shiftPreferences, dayPreferences, frequencyPreferences, positionPreferences } =
    useSchedulingPrefsConfig();
  const { submitSchedulingPreferences, isSubmitting } =
    useSchedulingPreferencesSubmit(resolvedOnSuccess);

  const availableLabelOptions = buildOptions(
    professionalSaasDetail?.available_job_slot_labels,
    (label) => label,
    (label) => label,
  );

  const positionOptions = buildOptions(
    currentUser?.professional?.positions,
    (position) => position.display_name,
    (position) => String(position.id),
  );

  const showShiftPreferences = shiftPreferences && availableLabelOptions.length > 1;
  const showDayPreferences = dayPreferences;
  const showFrequencyPreferences = frequencyPreferences;
  const showPositionPreferences = positionPreferences && positionOptions.length > 1;

  const savedLabelValues = (professionalSaasDetail?.preferred_job_slots ?? [])
    .map((slot) => (slot as ProfessionalLocationJobSlot).label)
    .filter((label): label is string => label !== null && label !== undefined);

  const savedPositionIds = (professionalSaasDetail?.preferred_positions ?? []).map(
    (preferredPosition) => preferredPosition.id,
  );

  const defaultValues: SchedulingPreferencesFormValues = {
    labels: savedLabelValues,
    workdays: (professionalSaasDetail?.preferred_workdays ?? []) as string[],
    positions: savedPositionIds.map(String),
    preferred_weekly_total_shifts: professionalSaasDetail?.preferred_weekly_total_shifts ?? 1,
  };

  const lastUpdatedByAccountId = professionalSaasDetail?.preferences_last_updated_by_account_id;

  const newLastUpdatedCompanyName =
    professionalSaasDetail?.preferences_last_updated_by_account?.company?.name.trim() ?? '';

  const lastUpdatedAccountName =
    lastUpdatedByAccountId === currentUser?.id
      ? `${currentUser?.first_name ?? ''} ${currentUser?.last_name ?? ''}`.trim()
      : newLastUpdatedCompanyName;

  const lastUpdatedText = formatLastUpdatedText(
    professionalSaasDetail?.preferences_last_updated_at,
    lastUpdatedAccountName,
  );

  const handleSubmit = (
    values: SchedulingPreferencesFormValues,
    initialValues: SchedulingPreferencesFormValues,
  ) => {
    if (!hasFormChanged(values, initialValues)) {
      resolvedOnSuccess();
      return;
    }

    submitSchedulingPreferences({
      labels: values.labels,
      workdays: values.workdays,
      position_ids: values.positions.map(Number),
      preferred_weekly_total_shifts: values.preferred_weekly_total_shifts,
    });
  };

  return {
    isLoading,
    isSubmitting,
    defaultValues,
    availableLabelOptions,
    positionOptions,
    showShiftPreferences,
    showDayPreferences,
    showFrequencyPreferences,
    showPositionPreferences,
    lastUpdatedText,
    handleSubmit,
  };
};
